/* ============================================================
   contact-terminal.js — single vault door (GAD-896 / GAD-9xx v4)
   ONE steel slab hinged on the left with ONE central spinner
   wheel. Page load: wheel spins (unlock) -> door swings open ->
   greeting types. Submit success: wheel spins -> door opens ->
   "transmitting packet" + packet anim -> door swings SHUT ->
   wheel spins back (lock) -> confirmation types on a plaque
   stamped on the sealed door.

   State classes (on .contact-terminal__doors):
     .is-unlocking ... wheel spins forward (CSS 1.4s)
     .is-open ........ slab swings out on the hinge (CSS 2.2s)
     .is-locking ..... wheel spins back after re-seal
   On root: .contact-terminal--doors-closing (60ms thud)
   Plaque: .contact-terminal__door-plaque.is-lit
   ============================================================ */
(function () {
  'use strict';

  var CHAR_DELAY_MS            = 40;
  var SUCCESS_CHAR_DELAY_MS    = 45;
  var WHEEL_SPIN_MS            = 1400; /* matches CSS wheel-spin animation */
  var DOOR_SWING_MS            = 2200; /* matches CSS door transition */
  var PAUSE_AFTER_OPEN_MS      = 500;
  var PAUSE_AFTER_SEAL_MS      = 350;
  var TX_CHAR_DELAY_MS         = 20;
  var PACKET_TRAVEL_MS         = 1800; /* matches CSS packet keyframes */
  var PACKET_FADE_MS           = 500;
  var TX_LEAD_LINE             = '> transmitting packet…';

  function pause(ms) {
    return new Promise(function (resolve) { window.setTimeout(resolve, ms); });
  }

  function prefersReducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (e) { return false; }
  }

  function clearCursor(el) {
    if (!el) return;
    var cursor = el.querySelector('.contact-terminal__cursor');
    if (cursor) cursor.parentNode.removeChild(cursor);
  }

  function appendCursor(el) {
    if (!el) return;
    clearCursor(el);
    var cursor = document.createElement('span');
    cursor.className = 'contact-terminal__cursor';
    el.appendChild(cursor);
  }

  function typeLine(el, text, perChar, abortRef) {
    return new Promise(function (resolve) {
      if (!el) { resolve(); return; }
      clearCursor(el);
      el.textContent = '';
      if (!text) { resolve(); return; }
      var i = 0;
      var aborted = false;
      abortRef.fn = function () { aborted = true; resolve(); };
      (function tick() {
        if (aborted) return;
        el.textContent = text.slice(0, i);
        i++;
        if (i <= text.length) {
          window.setTimeout(tick, perChar);
        } else {
          abortRef.fn = null;
          resolve();
        }
      })();
    });
  }

  function openDoorsInstant(doors) {
    if (!doors) return;
    var panels = doors.querySelectorAll('.contact-terminal__door');
    var saved = [];
    panels.forEach(function (p, idx) {
      saved[idx] = p.style.transition;
      p.style.transition = 'none';
    });
    doors.classList.remove('is-unlocking');
    doors.classList.remove('is-locking');
    doors.classList.add('is-open');
    void doors.offsetHeight;
    panels.forEach(function (p, idx) { p.style.transition = saved[idx] || ''; });
  }

  function initTerminal(root) {
    if (root.dataset.contactTerminalSequencer === 'true') return;
    root.dataset.contactTerminalSequencer = 'true';

    var doors      = root.querySelector('.contact-terminal__doors');
    var plaque     = root.querySelector('[data-contact-terminal-plaque]');
    var greeting   = root.querySelector('[data-contact-terminal-greeting]');
    var replay     = root.querySelector('[data-contact-terminal-replay]');
    var stopBtn    = root.querySelector('[data-contact-terminal-stop]');
    var form       = root.querySelector('form');
    var tsInput    = root.querySelector('[data-contact-terminal-timestamp]');
    var successEl  = root.querySelector('.contact-terminal__success[data-success="1"]');
    var firstInput = form && form.querySelector('input[name="contact[name]"]');

    var greetingLine = root.getAttribute('data-greeting-line') || '';
    var successLine  = root.getAttribute('data-success-line') ||
                       '> PACKET RECEIVED — transmission confirmed';

    var reducedMotion = prefersReducedMotion();
    var abortRef = { fn: null };
    /* Bumped on every (re)start so an in-flight sequence can detect it was
       superseded (replay) or cancelled (stop) and bail out of its awaits. */
    var playToken = 0;

    if (tsInput) tsInput.value = String(Date.now());

    /* ---- Form submit: anti-spam guard + Klaviyo tracking (native POST proceeds) ---- */
    if (form) {
      form.addEventListener('submit', function (event) {
        var honeypot = form.querySelector('input[name="contact[honeypot]"]');
        var stamped  = tsInput && tsInput.value ? parseInt(tsInput.value, 10) : 0;

        if ((honeypot && honeypot.value) || !stamped || Date.now() - stamped < 3000) {
          event.preventDefault();
          return;
        }

        var nameVal  = (form.querySelector('input[name="contact[name]"]')    || {}).value || '';
        var emailVal = (form.querySelector('input[name="contact[email]"]')   || {}).value || '';
        var bodyVal  = (form.querySelector('textarea[name="contact[body]"]') || {}).value || '';

        try {
          window.klaviyo = window.klaviyo || [];
          window.klaviyo.push(['identify', { $email: emailVal, $first_name: nameVal }]);
          window.klaviyo.push(['track', 'Submitted Contact Form', {
            name: nameVal, email: emailVal, message: bodyVal
          }]);
        } catch (e) { /* best-effort */ }
        /* native form POST proceeds */
      });
    }

    /* ---- Controls ---- */
    if (replay) {
      replay.addEventListener('click', function () {
        if (abortRef.fn) abortRef.fn();
        playToken++;
        resetDoorState();
        clearCursor(greeting);
        if (greeting) greeting.textContent = '';
        if (reducedMotion) { skipOpen(); return; }
        /* let the closed state paint, then run the full open */
        window.setTimeout(runCinematic, 80);
      });
    }

    if (stopBtn) {
      stopBtn.addEventListener('click', function () {
        /* Halt THIS play only — snap to the open console. No persistence. */
        if (abortRef.fn) abortRef.fn();
        playToken++;
        root.classList.remove('contact-terminal--doors-closing');
        openDoorsInstant(doors);
        if (greeting) {
          greeting.textContent = greetingLine;
          appendCursor(greeting);
        }
        focusFirst();
      });
    }

    /* ---- Entry point ---- */
    if (successEl) {
      handleSuccess();
    } else if (reducedMotion) {
      skipOpen();
    } else {
      runCinematic();
    }

    function resetDoorState() {
      root.classList.remove('contact-terminal--doors-closing');
      if (doors) {
        doors.classList.remove('is-open');
        doors.classList.remove('is-unlocking');
        doors.classList.remove('is-locking');
      }
      if (plaque) {
        plaque.classList.remove('is-lit');
        clearCursor(plaque);
        plaque.textContent = '';
      }
    }

    function focusFirst() {
      if (!firstInput) return;
      try { firstInput.focus({ preventScroll: true }); } catch (e) { firstInput.focus(); }
    }

    /* Spin the wheel, then swing the slab open. */
    async function unlockAndOpen(token) {
      if (!doors) return true;
      doors.classList.remove('is-locking');
      doors.classList.add('is-unlocking');
      await pause(WHEEL_SPIN_MS);
      if (token !== playToken) return false;

      doors.classList.add('is-open');
      await pause(DOOR_SWING_MS);
      if (token !== playToken) return false;

      doors.classList.remove('is-unlocking');
      return true;
    }

    /* Swing the slab shut, thud, then spin the wheel back to lock. */
    async function sealAndLock(token) {
      if (!doors) return true;
      doors.classList.remove('is-open');
      await pause(DOOR_SWING_MS);
      if (token !== playToken) return false;

      root.classList.add('contact-terminal--doors-closing');
      await pause(120);
      if (token !== playToken) return false;
      root.classList.remove('contact-terminal--doors-closing');

      doors.classList.add('is-locking');
      await pause(WHEEL_SPIN_MS);
      if (token !== playToken) return false;
      doors.classList.remove('is-locking');
      return true;
    }

    async function runCinematic() {
      var token = ++playToken;

      /* Act 1 — wheel spins, slab swings open (page is fully loaded). */
      if (!(await unlockAndOpen(token))) return;

      /* Act 2 — brief quiet beat. */
      if (greeting) greeting.textContent = '';
      await pause(PAUSE_AFTER_OPEN_MS);
      if (token !== playToken) return;

      /* Act 3 — type the greeting. */
      await typeLine(greeting, greetingLine, CHAR_DELAY_MS, abortRef);
      if (token !== playToken) return;
      appendCursor(greeting);

      /* Act 4 — focus the first field. */
      focusFirst();
    }

    function skipOpen() {
      ++playToken;
      openDoorsInstant(doors);
      if (greeting) {
        greeting.textContent = greetingLine;
        appendCursor(greeting);
      }
      focusFirst();
    }

    /* Packet-send: a datagram slides down a dashed wire and bursts at the
       far end. Element is created on demand and removed after the fade. */
    function playPacketSend() {
      return new Promise(function (resolve) {
        if (!greeting || !greeting.parentNode) { resolve(); return; }
        var wire = document.createElement('div');
        wire.className = 'contact-terminal__wire';
        wire.setAttribute('aria-hidden', 'true');
        wire.innerHTML =
          '<span class="contact-terminal__wire-line"></span>' +
          '<span class="contact-terminal__wire-packet"></span>' +
          '<span class="contact-terminal__wire-burst"></span>';
        greeting.parentNode.insertBefore(wire, greeting.nextSibling);
        void wire.offsetHeight; /* paint the resting state before animating */
        wire.classList.add('is-sending');
        window.setTimeout(function () {
          wire.classList.add('is-done');
          window.setTimeout(function () {
            if (wire.parentNode) wire.parentNode.removeChild(wire);
            resolve();
          }, PACKET_FADE_MS);
        }, PACKET_TRAVEL_MS);
      });
    }

    async function handleSuccess() {
      var token = ++playToken;

      if (reducedMotion) {
        openDoorsInstant(doors);
        if (greeting) {
          greeting.textContent = successLine;
          appendCursor(greeting);
        }
        return;
      }

      /* Act 1 — vault opens exactly like the intro. */
      if (!(await unlockAndOpen(token))) return;

      /* Act 2 — the packet goes in. */
      await typeLine(greeting, TX_LEAD_LINE, TX_CHAR_DELAY_MS, abortRef);
      if (token !== playToken) return;

      await playPacketSend();
      if (token !== playToken) return;

      /* Act 3 — door swings SHUT and the wheel locks it in. */
      if (!(await sealAndLock(token))) return;
      await pause(PAUSE_AFTER_SEAL_MS);
      if (token !== playToken) return;

      /* Act 4 — only NOW does it tell you it's sent: confirmation types
         out on a plaque stamped on the sealed door. */
      if (plaque) {
        plaque.classList.add('is-lit');
        await typeLine(plaque, successLine, SUCCESS_CHAR_DELAY_MS, abortRef);
        if (token !== playToken) return;
        appendCursor(plaque);
      } else if (greeting) {
        greeting.textContent = successLine;
        appendCursor(greeting);
      }
    }
  }

  function bootAll() {
    document.querySelectorAll('[data-contact-terminal]').forEach(function (root) {
      try {
        initTerminal(root);
      } catch (e) {
        /* Safety: never leave the vault sealed if the sequencer throws. */
        var doors = root.querySelector('.contact-terminal__doors');
        if (doors) doors.classList.add('is-open');
      }
    });
  }

  /* Open "as soon as the page is fully loaded" — wait for window load
     (bezel image included) rather than DOMContentLoaded. */
  if (document.readyState === 'complete') {
    bootAll();
  } else {
    window.addEventListener('load', bootAll, { once: true });
  }
})();
